/**
 * PublishApp — a focused compose window for authoring Nostr events.
 *
 * Currently supports kind 1 (short text notes).
 * Designed to grow: the "kind selector" at the top will later let authors
 * switch between note / article / list / calendar event / etc.
 */
import { useState, useRef, useCallback } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useToast } from '@/hooks/useToast';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { LoginArea } from '@/components/auth/LoginArea';
import { genUserName } from '@/lib/genUserName';
import { nip19 } from 'nostr-tools';
import {
  Send,
  Image,
  Hash,
  Globe,
  X,
  Loader2,
  AtSign,
} from 'lucide-react';

// ─── Character counter ────────────────────────────────────────────────────────

const SOFT_LIMIT = 280;

function CharCount({ count }: { count: number }) {
  const over = count > SOFT_LIMIT;
  const near = count > SOFT_LIMIT * 0.85;
  const remaining = SOFT_LIMIT - count;

  return (
    <span
      className={`text-[11px] tabular-nums transition-colors ${
        over ? 'text-destructive font-semibold' : near ? 'text-amber-500' : 'text-muted-foreground'
      }`}
    >
      {over ? `+${Math.abs(remaining)}` : remaining}
    </span>
  );
}

// ─── Mention suggestions ──────────────────────────────────────────────────────

interface MentionSuggestion {
  pubkey: string;
  name: string;
  picture?: string;
  nip05?: string;
}

function MentionDropdown({
  query,
  onSelect,
}: {
  query: string;
  onSelect: (suggestion: MentionSuggestion) => void;
}) {
  const { nostr } = useNostr();

  const { data: suggestions } = useQuery({
    queryKey: ['mention-search', query],
    queryFn: async ({ signal }) => {
      if (!query || query.length < 2) return [];
      const results = await nostr.query(
        [{ kinds: [0], search: query, limit: 5 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(3000)]) }
      );
      return results
        .map((e) => {
          try {
            const meta = JSON.parse(e.content) as {
              name?: string;
              display_name?: string;
              picture?: string;
              nip05?: string;
            };
            return {
              pubkey: e.pubkey,
              name: meta.name ?? meta.display_name ?? genUserName(e.pubkey),
              picture: meta.picture,
              nip05: meta.nip05,
            };
          } catch {
            return { pubkey: e.pubkey, name: genUserName(e.pubkey) };
          }
        })
        .filter((s) => s.name.toLowerCase().includes(query.toLowerCase()));
    },
    enabled: query.length >= 2,
    staleTime: 10000,
  });

  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div className="absolute z-50 left-0 right-0 top-full mt-1 rounded-lg border border-border bg-popover shadow-xl overflow-hidden">
      {suggestions.map((s) => (
        <button
          key={s.pubkey}
          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-muted transition-colors text-left"
          onMouseDown={(e) => { e.preventDefault(); onSelect(s); }}
        >
          <Avatar className="w-6 h-6 shrink-0">
            <AvatarImage src={s.picture} alt={s.name} />
            <AvatarFallback className="text-[8px]">{s.name.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <span className="text-xs font-medium truncate block">{s.name}</span>
            {s.nip05 && (
              <span className="text-[10px] text-muted-foreground truncate block">{s.nip05}</span>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}

// ─── Attached media preview ───────────────────────────────────────────────────

function MediaPreview({
  urls,
  onRemove,
}: {
  urls: string[];
  onRemove: (url: string) => void;
}) {
  if (urls.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 px-4 pt-2">
      {urls.map((url) => (
        <div key={url} className="relative group rounded-lg overflow-hidden w-20 h-20 bg-muted border border-border/50">
          <img src={url} alt="" className="w-full h-full object-cover" />
          <button
            className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => onRemove(url)}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Main PublishApp ──────────────────────────────────────────────────────────

export function PublishApp() {
  const { user, metadata } = useCurrentUser();
  const { mutateAsync: publish } = useNostrPublish();
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadFile();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [content, setContent] = useState('');
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [isPublishing, setIsPublishing] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState<number | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const displayName = metadata?.name ?? metadata?.display_name ?? (user ? genUserName(user.pubkey) : '');

  // Auto-grow textarea
  const handleInput = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 300)}px`;
  }, []);

  // Track @mention as user types
  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setContent(val);

    const cursor = e.target.selectionStart ?? val.length;
    // Find the last @ before the cursor
    const before = val.slice(0, cursor);
    const atMatch = before.match(/@(\w*)$/);
    if (atMatch) {
      setMentionStart(cursor - atMatch[0].length);
      setMentionQuery(atMatch[1]);
    } else {
      setMentionStart(null);
      setMentionQuery('');
    }
    handleInput();
  };

  const insertMention = useCallback(
    (suggestion: { pubkey: string; name: string }) => {
      if (mentionStart === null) return;
      const npub = nip19.npubEncode(suggestion.pubkey);
      const before = content.slice(0, mentionStart);
      const after = content.slice(textareaRef.current?.selectionStart ?? content.length);
      const inserted = `nostr:${npub} `;
      setContent(before + inserted + after);
      setMentionStart(null);
      setMentionQuery('');
      setTimeout(() => {
        const el = textareaRef.current;
        if (!el) return;
        const pos = before.length + inserted.length;
        el.setSelectionRange(pos, pos);
        el.focus();
        handleInput();
      }, 0);
    },
    [content, mentionStart, handleInput]
  );

  // Extract hashtags from content to add as 't' tags
  const extractHashtags = (text: string): string[] => {
    const matches = text.match(/#(\w+)/g) ?? [];
    return [...new Set(matches.map((h) => h.slice(1).toLowerCase()))];
  };

  // Extract nostr: mentions to add as 'p' tags
  const extractMentionedPubkeys = (text: string): string[] => {
    const matches = text.match(/nostr:(npub1[a-z0-9]+)/g) ?? [];
    const pubkeys: string[] = [];
    for (const m of matches) {
      try {
        const decoded = nip19.decode(m.replace('nostr:', ''));
        if (decoded.type === 'npub') pubkeys.push(decoded.data);
      } catch { /* skip */ }
    }
    return [...new Set(pubkeys)];
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    try {
      for (const file of files) {
        const tags = await uploadFile(file);
        setMediaUrls((prev) => [...prev, tags[0][1]]);
      }
    } catch {
      toast({ title: 'Upload failed', variant: 'destructive' });
    }
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePublish = async () => {
    if (!user || (!content.trim() && mediaUrls.length === 0)) return;
    setIsPublishing(true);
    try {
      // Build content: append media URLs at the end
      const fullContent = [
        content.trim(),
        ...mediaUrls,
      ].filter(Boolean).join('\n\n');

      // Build tags
      const tags: string[][] = [];

      // Hashtags → 't' tags
      for (const tag of extractHashtags(content)) {
        tags.push(['t', tag]);
      }

      // Mentioned pubkeys → 'p' tags
      for (const pk of extractMentionedPubkeys(content)) {
        tags.push(['p', pk]);
      }

      await publish({ kind: 1, content: fullContent, tags });

      toast({ title: '✓ Published', description: 'Your note is live on Nostr.' });
      setContent('');
      setMediaUrls([]);
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      // Invalidate feeds so the new note appears
      queryClient.invalidateQueries({ queryKey: ['feed'] });
    } catch (err) {
      toast({ title: 'Publish failed', description: String(err), variant: 'destructive' });
    } finally {
      setIsPublishing(false);
    }
  };

  const removeMedia = (url: string) => setMediaUrls((prev) => prev.filter((u) => u !== url));

  const canPublish = (content.trim().length > 0 || mediaUrls.length > 0) && !isPublishing && !isUploading;

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
          <Send className="w-7 h-7 text-primary" />
        </div>
        <div>
          <p className="text-sm font-semibold mb-1">Log in to publish</p>
          <p className="text-xs text-muted-foreground">Sign in to share notes with the Nostr network.</p>
        </div>
        <LoginArea className="w-48" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Kind selector — future home for article / list / etc. */}
      <div className="flex items-center gap-1.5 px-4 py-2 border-b border-border/50 bg-muted/20 shrink-0">
        <button className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
          <Globe className="w-3 h-3" />
          Note
        </button>
        {/* Future kinds will appear here */}
        <span className="text-[10px] text-muted-foreground ml-1">More kinds coming soon</span>
      </div>

      {/* Compose area */}
      <div className="flex-1 overflow-auto min-h-0">
        <div className="flex gap-3 px-4 pt-4">
          {/* Avatar */}
          <Avatar className="w-9 h-9 shrink-0 mt-0.5">
            <AvatarImage src={metadata?.picture} alt={displayName} />
            <AvatarFallback className="text-xs">{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>

          {/* Text + mention dropdown */}
          <div className="flex-1 relative">
            <Textarea
              ref={textareaRef}
              value={content}
              onChange={handleContentChange}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setMentionStart(null);
                  setMentionQuery('');
                }
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handlePublish();
                }
              }}
              placeholder="What's happening on Nostr?"
              className="w-full min-h-[120px] resize-none border-0 bg-transparent focus-visible:ring-0 p-0 text-sm leading-relaxed placeholder:text-muted-foreground/60"
              autoFocus
            />
            {/* @ mention dropdown */}
            {mentionStart !== null && mentionQuery.length >= 2 && (
              <MentionDropdown query={mentionQuery} onSelect={insertMention} />
            )}
          </div>
        </div>

        {/* Media previews */}
        <MediaPreview urls={mediaUrls} onRemove={removeMedia} />

        {/* Hashtag hint — appears when user types # */}
        {content.includes('#') && (
          <div className="px-4 pt-2">
            <div className="flex flex-wrap gap-1">
              {extractHashtags(content).map((tag) => (
                <span key={tag} className="inline-flex items-center gap-0.5 text-[11px] text-primary/80 bg-primary/8 px-1.5 py-0.5 rounded-full">
                  <Hash className="w-2.5 h-2.5" />
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Toolbar + publish */}
      <div className="border-t border-border/50 bg-muted/10 shrink-0">
        <div className="flex items-center gap-1 px-3 py-2">
          {/* Media upload */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={handleUpload}
          />
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            title="Attach image or video"
          >
            {isUploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Image className="w-4 h-4" />
            )}
          </Button>

          {/* @ mention trigger */}
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-primary"
            onClick={() => {
              setContent((c) => c + '@');
              setTimeout(() => textareaRef.current?.focus(), 0);
            }}
            title="Mention someone"
          >
            <AtSign className="w-4 h-4" />
          </Button>

          {/* Hashtag trigger */}
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-primary"
            onClick={() => {
              setContent((c) => c + '#');
              setTimeout(() => textareaRef.current?.focus(), 0);
            }}
            title="Add hashtag"
          >
            <Hash className="w-4 h-4" />
          </Button>

          <div className="flex-1" />

          {/* Character count */}
          <CharCount count={content.length} />

          {/* Spacer */}
          <div className="w-px h-4 bg-border/50 mx-1" />

          {/* Publish button */}
          <Button
            size="sm"
            className="h-8 px-4 gap-1.5 font-semibold"
            onClick={handlePublish}
            disabled={!canPublish}
          >
            {isPublishing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            Publish
          </Button>
        </div>

        {/* Keyboard shortcut hint */}
        <div className="px-4 pb-2">
          <span className="text-[9px] text-muted-foreground/60">⌘↵ to publish · @name to mention · #tag for topics</span>
        </div>
      </div>
    </div>
  );
}
